import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { OktaAuthStateService } from '@okta/okta-angular';
import { map } from 'rxjs';
import { AiChatShellComponent } from '../../../features/ai-chat/ai-chat-shell.component';
import { AiChatPanelService } from '../../../features/ai-chat/services/ai-chat-panel.service';
import { QaControlsComponent } from '../../../features/qa-controls/qa-controls';
import { QaPanelService } from '../../../features/qa-controls/qa-panel.service';
import {
    FeatureFlagService,
    KnownFeatureFlags,
} from '../../utils/feature-flags/feature-flag.service';
import { FooterComponent } from '../footer/footer.component';
import { HeaderContainerComponent } from '../header-container/header-container.component';

@Component({
    selector: 'acm-layout',
    imports: [
        RouterOutlet,
        HeaderContainerComponent,
        FooterComponent,
        QaControlsComponent,
        AiChatShellComponent,
    ],
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'd-flex flex-column vh-100',
        '[class.qa-panel-open]': 'qaPanel.isOpen() && qaPanel.isDocked()',
        '[class.qa-panel-start]': "qaPanel.side() === 'start'",
        '(document:keydown)': 'onDocumentKeydown($event)',
    },
})
export class LayoutComponent {
    protected readonly featureFlags = inject(FeatureFlagService);
    protected readonly flags = KnownFeatureFlags;
    protected readonly qaPanel = inject(QaPanelService);
    private readonly aiChatPanel = inject(AiChatPanelService);
    private readonly authStateService = inject(OktaAuthStateService);

    protected readonly isAuthenticated = toSignal(
        this.authStateService.authState$.pipe(map((s) => !!s?.isAuthenticated)),
        { initialValue: false }
    );

    constructor() {
        effect(() => {
            if (!this.isAuthenticated()) {
                this.aiChatPanel.close();
            }
        });
    }

    protected onDocumentKeydown(event: KeyboardEvent): void {
        if (!this.featureFlags.isFlagEnabled(KnownFeatureFlags.aiChat) || !this.isAuthenticated()) {
            return;
        }
        if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'a') {
            event.preventDefault();
            this.aiChatPanel.toggle();
        }
    }
}
